/**
 * New Year party invitations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearPartyInvitationsPack: ToilePack = {
    slug: 'new-year-party-invitations',
    category: 'season',
    name: 'New Year party invitations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'midnight', label: 'Midnight party', subject: 'A vertical invitation card lettered "NEW YEAR\'S EVE" over fireworks, a large blank panel for the details'},
        {key: 'dinner', label: 'Dinner', subject: 'A vertical invitation card lettered "NEW YEAR\'S DINNER" in an art deco frame, a blank panel for date and place'},
        {key: 'family', label: 'Family countdown', subject: 'A vertical invitation card lettered "COUNTDOWN PARTY" with cartoon balloons and a clock, a blank panel'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a dark page with a gold border and a margin'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical page with a confetti border and a white margin'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a clean page with wide margins'},
    ],
    directions: ["for a kids' countdown party instead", 'with a champagne-toast illustration', 'as a square digital invite'],
    directionHint: "try: a kids' countdown party instead",
    seo: {
        title: 'New Year Invitations: Midnight, Dinner & Family',
        metaDescription: "Nine New Year's Eve invitations — a midnight party, a dinner, and a family countdown — with a blank details panel, in three festive styles.",
        h1: 'New Year invitations: midnight party, dinner, family',
        tagline: 'Nine cards, three party types, three styles each — greeting lettered, details left blank.',
        intro: "Three New Year's Eve invitations — a midnight party, a formal dinner, and a family countdown — each with the greeting lettered and a blank panel for the host to fill in, in black-and-gold, confetti-party, and minimal styles. Ask for a different party type, a couple's names added, or a QR-code space, and the same invitation grammar carries it.",
        useCases: [
            "a New Year's Eve party invite",
            'a formal NYE dinner invitation',
            'a family countdown party for kids',
            'a neighborhood open-house invite',
        ],
        faq: [
            {q: 'Is the date and time already filled in?', a: 'No — the details panel prints blank; add the date, time, and address by hand or with a second pass.'},
            {q: 'What size do these print at?', a: 'Each card is a single vertical page, close to US letter or A4 proportions.'},
            {q: 'Can I get a different party type?', a: 'Yes — the direction field takes any variation, including a different party type or venue.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
